// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";

import { View, Button, SchemaTable, Loader } from "@components";
import { AddFileAttachmentModal } from "@modules/shared/components";
import actionCreator from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import type { State } from "@store/reducers";
import type { RtwAttachment } from "@modules/service/reducer/rtw/types";

type OwnProps = $Exact<{
  processId: number,
  clientId: number,
  data: ?Array<RtwAttachment>,
  disabled?: boolean,
  onChange?: (data: Array<RtwAttachment>) => void,
}>;

type PropsFromState = $Exact<{
  showAddModal: boolean,
  addFileInProgress: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setShowAddModal: typeof actionCreator.SetShowAddRtwAttachmentModal,
  addAttachment: typeof actionCreator.AddRtwAttachment,
  deleteAttachment: typeof actionCreator.DeleteRtwAttachment,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class RtwNotificationAttachmentsForm extends React.Component<Props> {
  get data() {
    return this.props.data || [];
  }

  get schema() {
    return {
      type: "object",
      required: ["file"],
      properties: {
        file: {
          type: "string",
          title: "File",
          format: "data-url",
        },
      },
    };
  }

  get uiSchema() {
    return {
      "ui:order": ["id", "name"],
      id: {
        "ui:widget": "text",
        "ui:title": "Attachment ID",
        "ui:options": {
          minWidth: "15%",
          editable: false,
        },
      },
      name: {
        "ui:widget": "text",
        "ui:title": "File Name",
        "ui:options": {
          minWidth: "80%",
          editable: false,
        },
      },
      button: {
        "ui:title": " ",
        "ui:widget": "icon-button",
        "ui:options": {
          minWidth: "40px",
          icon: "delete",
          materialIcon: true,
          iconColor: "black",
          iconClass: "material-icons pointer font-size-26 ml-1 mr-1",
          onClick: (row: Object) => {
            this.handleDeleteAttachment(row.id);
          },
        },
      },
    };
  }

  showAddModal = () => {
    this.props.setShowAddModal(true);
  };

  closeAddModal = () => {
    this.props.setShowAddModal(false);
  };

  handleAddAttachment = (form: Object) => {
    this.props.addAttachment({
      clientId: this.props.clientId,
      processId: this.props.processId,
      form,
    });
  };

  handleDeleteAttachment = (id: number) => {
    this.props.deleteAttachment({
      clientId: this.props.clientId,
      processId: this.props.processId,
      attachmentId: id,
    });
  };

  render() {
    return (
      <View>
        <View direction="row">
          <View flex="1">
            <h6>{this.props.t("RETURN_TO_WORK_DETAILS_NOTIFICATION_ATTACHMENTS")}</h6>
          </View>
          {!this.props.disabled && (
            <Button
              materialIcon="add"
              className="link alert-sgs__add-button"
              onClick={this.showAddModal}
              label={this.props.t("RETURN_TO_WORK_DETAILS_ADD_ATTACHMENT")}
            />
          )}
        </View>

        <View gap={6}>
          <View>
            {this.schema && this.uiSchema ? (
              <SchemaTable
                tableData={this.data}
                schema={this.schema}
                uiSchema={this.uiSchema}
                disabled={!!this.props.disabled}
              />
            ) : null}
          </View>
        </View>
        {this.props.showAddModal && (
          <AddFileAttachmentModal
            title="RETURN_TO_WORK_DETAILS_ADD_ATTACHMENT"
            clientId={this.props.clientId}
            onCancel={this.closeAddModal}
            onSubmit={this.handleAddAttachment}
            disableAdd={this.props.addFileInProgress}
          />
        )}
        {this.props.addFileInProgress && <Loader />}
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  showAddModal: serviceSelectors.getShowRtwAddAttachmentModal(state),
  addFileInProgress: serviceSelectors.getAddFileInProgress(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  setShowAddModal: actionCreator.SetShowAddRtwAttachmentModal,
  addAttachment: actionCreator.AddRtwAttachment,
  deleteAttachment: actionCreator.DeleteRtwAttachment,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(RtwNotificationAttachmentsForm);
